import { useMemo, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type VisibilityState,
  type ColumnPinningState,
  type ColumnSizingState,
  type OnChangeFn,
  type RowSelectionState,
  type Row,
} from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import type { ListViewColumnConfig } from '@/lib/views/listViewSchemas/types';
import type { AdvancedTableState } from './useAdvancedTableState';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { DataTableSkeleton } from '@/components/data-table/data-table-skeleton';
import { AdvancedTableToolbar } from './AdvancedTableToolbar';
import { AdvancedFilterList } from './AdvancedFilterList';
import { AdvancedActionBar } from './AdvancedActionBar';
import { exportSelectedRowsToCSV } from './advancedTableUtils';
import { DEFAULT_ADVANCED_TABLE_LABELS, type AdvancedTableLabels } from './advancedTableLabels';
import { buildAdvancedColumns } from './buildAdvancedColumns';
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePreferencesStore } from '@/lib/store/preferencesStore';
import { frappe } from '@/lib/frappe-client';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useDataGrid } from '@/components/data-grid/useDataGrid';
import { DataGridPasteDialog } from '@/components/data-grid/DataGridPasteDialog';
import { DataGridStandardBody, DataGridVirtualBody } from '@/components/data-grid/DataGridTableBody';
import { useDataGridVirtualization } from '@/components/data-grid/useDataGridVirtualization';
import { DEFAULT_DATA_GRID_LABELS, type DataGridLabels } from '@/components/data-grid/dataGridLabels';
import { getCommonPinningStyles } from '@/lib/data-table';
import { useErpLayout } from '@/app/layouts/ErpLayoutContext';

// Stable empty array for Zustand selector — avoids new [] on every render
const EMPTY_HIDDEN_COLUMNS: string[] = [];
// Stable empty pinning state
const EMPTY_PINNING: { left: string[]; right: string[] } = { left: [], right: [] };
// Stable empty sizing state
const EMPTY_SIZING: Record<string, number> = {};
// Stable empty Set for data grid — avoids new Set() on every render
const EMPTY_EDITABLE_FIELDS = new Set<string>();

interface AdvancedTableViewProps<T> {
  items: T[];
  total: number;
  isLoading: boolean;
  columns: ListViewColumnConfig[];
  doctype?: string;
  onRowClick?: (row: T) => void;
  getRowId?: (row: T) => string;
  pageSizeOptions?: number[];
  advancedState: AdvancedTableState;
  onMutationSuccess?: () => void;
  labels?: Partial<AdvancedTableLabels>;
  columnOverrides?: Record<string, (props: { row: Row<T> }) => ReactNode>;
  /** Pre-fetched select options per fieldname for filter dropdowns */
  selectOptions?: Record<string, { label: string; value: string }[]>;
  /** Set of fieldnames that are editable in data grid mode */
  editableFields?: Set<string>;
  /** i18n labels for data grid UI strings */
  dataGridLabels?: Partial<DataGridLabels>;
  /** Callback when focus mode changes (auto-coupled with edit mode) */
  onFocusModeChange?: (focused: boolean) => void;
  /** Whether "Show archived" is active — controls Archive vs Restore in action bar */
  showArchived?: boolean;
}

export function AdvancedTableView<T>({
  items,
  total,
  isLoading,
  columns: schemaColumns,
  doctype,
  onRowClick,
  getRowId,
  pageSizeOptions = [20, 50, 100, 200, 400, 500],
  advancedState,
  onMutationSuccess,
  labels,
  columnOverrides,
  selectOptions,
  editableFields,
  dataGridLabels,
  onFocusModeChange,
  showArchived,
}: AdvancedTableViewProps<T>) {
  const queryClient = useQueryClient();
  const l = { ...DEFAULT_ADVANCED_TABLE_LABELS, ...labels };
  const dgl = { ...DEFAULT_DATA_GRID_LABELS, ...dataGridLabels };
  const [filtersOpen, setFiltersOpen] = useState(
    () => advancedState.filterRows.length > 0,
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Data grid edit mode + focus mode (auto-coupled)
  const [isEditMode, setIsEditMode] = useState(false);
  const [isFocusMode, setIsFocusMode] = useState(false);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const showEditToggle = !isMobile && total > 0 && !!editableFields && editableFields.size > 0;

  const toggleEditMode = useCallback(() => {
    setIsEditMode((prev) => {
      const next = !prev;
      if (!isMobile) setIsFocusMode(next);
      else setIsFocusMode(false);
      return next;
    });
  }, [isMobile]);

  useEffect(() => {
    onFocusModeChange?.(isFocusMode);
  }, [isFocusMode, onFocusModeChange]);

  // Sidebar collapse via ErpLayoutContext
  const { requestCollapse, releaseCollapse } = useErpLayout();
  useEffect(() => {
    if (isFocusMode) {
      requestCollapse('focus-mode');
      return () => releaseCollapse('focus-mode');
    }
  }, [isFocusMode, requestCollapse, releaseCollapse]);

  // Lock page scroll in focus mode — only the table work area scrolls
  useEffect(() => {
    if (!isFocusMode) return;
    // Scroll to top so toolbar stays visible when page scroll is locked
    window.scrollTo({ top: 0 });
    const html = document.documentElement;
    const prev = html.style.overflow;
    html.style.overflow = 'hidden';
    return () => { html.style.overflow = prev; };
  }, [isFocusMode]);

  // Clean up focus mode on unmount (navigation away)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    return () => {
      setIsEditMode(false);
      setIsFocusMode(false);
    };
  }, []);

  const exitFocusMode = useCallback(() => setIsFocusMode(false), []);

  // Focus mode screen reader announcement
  const [focusAnnouncement, setFocusAnnouncement] = useState('');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally omit isEditMode to avoid re-triggering
  useEffect(() => {
    if (isFocusMode) {
      setFocusAnnouncement('Entered focus mode');
    } else if (isEditMode) {
      setFocusAnnouncement('Exited focus mode');
    }
  }, [isFocusMode]);

  // Row density (global preference)
  const tableDensity = usePreferencesStore((s) => s.tableDensity ?? 'comfortable');

  // Virtualization scroll container
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Clear selection when page/sort/filters change
  const paginationKey = `${advancedState.pageIndex}-${advancedState.pageSize}`;
  const sortingKey = JSON.stringify(advancedState.sorting);
  const filterKey = JSON.stringify(advancedState.filterRows.map((r) => r.id));
  useEffect(() => {
    setRowSelection({});
    dataGrid.clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationKey, sortingKey, filterKey]);

  // Column visibility from preferencesStore
  const hiddenColumns = usePreferencesStore(
    useCallback(
      (s) => s.docTypeAdvancedColumns?.[doctype ?? ''] ?? EMPTY_HIDDEN_COLUMNS,
      [doctype],
    ),
  );
  const setDocTypeAdvancedColumns = usePreferencesStore(
    (s) => s.setDocTypeAdvancedColumns,
  );

  const columnVisibility: VisibilityState = useMemo(
    () => Object.fromEntries(hiddenColumns.map((c) => [c, false])),
    [hiddenColumns],
  );

  // Column pinning from preferencesStore (per-doctype)
  const storedPinning = usePreferencesStore(
    useCallback(
      (s) => s.docTypeColumnPinning?.[doctype ?? ''] ?? EMPTY_PINNING,
      [doctype],
    ),
  );
  const setDocTypeColumnPinning = usePreferencesStore(
    (s) => s.setDocTypeColumnPinning,
  );

  const onColumnVisibilityChange: OnChangeFn<VisibilityState> = useCallback(
    (updaterOrValue) => {
      if (!doctype) return;
      const next =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(columnVisibility)
          : updaterOrValue;
      const hidden = Object.entries(next)
        .filter(([, v]) => !v)
        .map(([k]) => k);
      setDocTypeAdvancedColumns(doctype, hidden);

      // Clean up pinning state: remove hidden columns from pinning
      const newLeft = storedPinning.left.filter((id) => !hidden.includes(id));
      const newRight = storedPinning.right.filter((id) => !hidden.includes(id));
      if (newLeft.length !== storedPinning.left.length || newRight.length !== storedPinning.right.length) {
        // Auto-remove __select if no other columns remain pinned left
        const hasLeftPinned = newLeft.some((id) => id !== '__select');
        const finalLeft = hasLeftPinned ? newLeft : newLeft.filter((id) => id !== '__select');
        setDocTypeColumnPinning(doctype, { left: finalLeft, right: newRight });
      }
    },
    [columnVisibility, doctype, setDocTypeAdvancedColumns, storedPinning, setDocTypeColumnPinning],
  );

  // Auto-pin __select when any column is pinned left
  const columnPinning: ColumnPinningState = useMemo(() => {
    const left = storedPinning.left;
    const right = storedPinning.right;
    const hasLeftPinned = left.some((id) => id !== '__select');
    if (hasLeftPinned && !left.includes('__select')) {
      return { left: ['__select', ...left], right };
    }
    if (!hasLeftPinned && left.includes('__select')) {
      return { left: left.filter((id) => id !== '__select'), right };
    }
    return { left, right };
  }, [storedPinning]);

  const onColumnPinningChange: OnChangeFn<ColumnPinningState> = useCallback(
    (updaterOrValue) => {
      if (!doctype) return;
      const next =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(columnPinning)
          : updaterOrValue;
      const left = next.left ?? [];
      const right = next.right ?? [];
      // Auto-add __select when any column is pinned left
      const hasLeftPinned = left.some((id) => id !== '__select');
      const finalLeft = hasLeftPinned && !left.includes('__select')
        ? ['__select', ...left]
        : !hasLeftPinned
          ? left.filter((id) => id !== '__select')
          : left;
      setDocTypeColumnPinning(doctype, { left: finalLeft, right });
    },
    [columnPinning, doctype, setDocTypeColumnPinning],
  );

  // Column sizing from preferencesStore (per-doctype)
  const storedSizing = usePreferencesStore(
    useCallback(
      (s) => s.docTypeColumnSizing?.[doctype ?? ''] ?? EMPTY_SIZING,
      [doctype],
    ),
  );
  const setDocTypeColumnSizing = usePreferencesStore(
    (s) => s.setDocTypeColumnSizing,
  );

  const columnSizing: ColumnSizingState = storedSizing;

  const onColumnSizingChange: OnChangeFn<ColumnSizingState> = useCallback(
    (updaterOrValue) => {
      if (!doctype) return;
      const next =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(columnSizing)
          : updaterOrValue;
      setDocTypeColumnSizing(doctype, next);
    },
    [columnSizing, doctype, setDocTypeColumnSizing],
  );

  const columnDefs = useMemo(
    () => buildAdvancedColumns<T>(schemaColumns, columnOverrides),
    [schemaColumns, columnOverrides],
  );

  const pageCount = Math.ceil(total / advancedState.pageSize) || 1;

  const table = useReactTable({
    data: items,
    columns: columnDefs,
    pageCount,
    state: {
      pagination: advancedState.pagination,
      sorting: advancedState.sorting,
      columnVisibility,
      columnPinning,
      columnSizing,
      rowSelection,
    },
    onPaginationChange: advancedState.onPaginationChange,
    onSortingChange: advancedState.setSorting,
    onColumnVisibilityChange,
    onColumnPinningChange,
    onColumnSizingChange,
    onRowSelectionChange: setRowSelection,
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    enableRowSelection: true,
    enableMultiSort: true,
    getRowId: getRowId ? (row) => getRowId(row) : undefined,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const visibleColumnIds = useMemo(
    () => table.getVisibleFlatColumns().map((c) => c.id).filter((id) => id !== '__select'),
    [columnVisibility],
  );

  const getRowData = useCallback(
    (rowIndex: number): Record<string, unknown> | null => {
      const row = table.getRowModel().rows[rowIndex];
      return row ? (row.original as Record<string, unknown>) : null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, advancedState.sorting],
  );

  const fieldTypeMap = useMemo(
    () => new Map(schemaColumns.map((c) => [c.fieldname, c.type as string | undefined])),
    [schemaColumns],
  );

  const rowCount = table.getRowModel().rows.length;

  const fieldLabelMap = useMemo(
    () => new Map(schemaColumns.map((c) => [c.fieldname, c.label ?? c.fieldname])),
    [schemaColumns],
  );

  const dataGrid = useDataGrid({
    editableFields: editableFields ?? EMPTY_EDITABLE_FIELDS,
    visibleColumnIds,
    rowCount,
    isEditMode,
    doctype: doctype ?? '',
    getRowData,
    getFieldLabel: useCallback(
      (fieldname: string) => fieldLabelMap.get(fieldname) ?? fieldname,
      [fieldLabelMap],
    ),
    scrollContainerRef,
  });

  const { virtualizer, isVirtualized } = useDataGridVirtualization({
    rowCount,
    parentRef: scrollContainerRef,
    enabled: isEditMode,
    density: tableDensity,
  });

  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const selectedCount = selectedRows.length;

  useEffect(() => {
    if (selectedCount === 0) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) setRowSelection({});
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [selectedCount]);

  // Focus the table container immediately when entering focus mode
  useEffect(() => {
    if (!isFocusMode) return;
    scrollContainerRef.current?.focus();
  }, [isFocusMode]);

  // Escape key exits focus mode (when no popover or cell editing active)
  useEffect(() => {
    if (!isFocusMode) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      // Don't steal Escape from Radix popovers/dialogs
      if (document.querySelector('[data-radix-popper-content-wrapper]')) return;
      // Don't steal Escape from active cell editing (data grid handles it)
      if (dataGrid.isCellEditing) return;
      setIsFocusMode(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isFocusMode, dataGrid.isCellEditing]);

  const toggleFilters = useCallback(() => setFiltersOpen((prev) => !prev), []);

  const activeFilterCount = useMemo(
    () => advancedState.filterRows.filter((r) => r.field && r.operator).length,
    [advancedState.filterRows],
  );

  const getSelectedNames = useCallback(
    (): string[] => selectedRows.map((r) => (r.original as { name: string }).name),
    [selectedRows],
  );

  const handleSetStatus = useCallback(async (disabled: 0 | 1) => {
    if (!doctype) return;
    const names = getSelectedNames();
    if (names.length === 0) return;
    try {
      const docs = names.map((n) => ({ doctype, docname: n, disabled }));
      const result = await frappe.call<{ failed_docs?: string[] }>(
        'frappe.client.bulk_update',
        { docs: JSON.stringify(docs) },
      );
      const failed = result?.failed_docs ?? [];
      if (failed.length > 0) {
        toast.error(`${failed.length} ${l.toastPartialFail}`);
      } else {
        toast.success(`${names.length} items ${disabled ? l.toastDisabled : l.toastEnabled}`);
      }
      setRowSelection({});
      onMutationSuccess?.();
    } catch {
      toast.error(l.toastFailedUpdate);
    }
  }, [doctype, getSelectedNames, onMutationSuccess, l]);

  const handleExportCSV = useCallback(() => {
    exportSelectedRowsToCSV(selectedRows, table.getVisibleFlatColumns(), doctype ?? 'export');
  }, [selectedRows, table, doctype]);

  const handleArchive = useCallback(async () => {
    if (!doctype) return;
    const names = getSelectedNames();
    if (names.length === 0) return;
    try {
      const docs = names.map((n) => ({ doctype, docname: n, disabled: 1 }));
      const result = await frappe.call<{ failed_docs?: string[] }>(
        'frappe.client.bulk_update',
        { docs: JSON.stringify(docs) },
      );
      const failed = result?.failed_docs ?? [];
      if (failed.length > 0) {
        toast.error(`${failed.length} ${l.toastPartialFail}`);
      } else {
        toast.success(`${names.length} items ${l.toastArchived}`);
      }
      setRowSelection({});
      onMutationSuccess?.();
      queryClient.invalidateQueries({ queryKey: ["archived-count"] });
    } catch {
      toast.error(l.toastFailedArchive);
    }
  }, [doctype, getSelectedNames, onMutationSuccess, l, queryClient]);

  const handleRestore = useCallback(async () => {
    if (!doctype) return;
    const names = getSelectedNames();
    if (names.length === 0) return;
    try {
      const docs = names.map((n) => ({ doctype, docname: n, disabled: 0 }));
      const result = await frappe.call<{ failed_docs?: string[] }>(
        'frappe.client.bulk_update',
        { docs: JSON.stringify(docs) },
      );
      const failed = result?.failed_docs ?? [];
      if (failed.length > 0) {
        toast.error(`${failed.length} ${l.toastPartialFail}`);
      } else {
        toast.success(`${names.length} items ${l.toastRestored}`);
      }
      setRowSelection({});
      onMutationSuccess?.();
      queryClient.invalidateQueries({ queryKey: ["archived-count"] });
    } catch {
      toast.error(l.toastFailedRestore);
    }
  }, [doctype, getSelectedNames, onMutationSuccess, l, queryClient]);

  const clearSelection = useCallback(() => setRowSelection({}), []);

  if (isLoading && items.length === 0) {
    return (
      <DataTableSkeleton
        columnCount={schemaColumns.length + 1}
        rowCount={Math.min(advancedState.pageSize, 10)}
        withPagination
        withViewOptions={false}
      />
    );
  }

  const bodyProps = {
    table,
    dataGrid,
    isEditMode,
    editableFields: editableFields ?? EMPTY_EDITABLE_FIELDS,
    fieldTypeMap,
    selectOptions,
    onRowClick,
    columnCount: columnDefs.length,
    emptyTitle: l.emptyTitle,
    emptyDescription: l.emptyDescription,
    labels: dgl,
  };

  return (
    <div className={cn(
      'flex flex-col gap-2.5',
      isFocusMode && 'h-[100dvh] fixed inset-0 z-40 bg-white dark:bg-background px-2 pt-1 pb-0',
    )}>
      <AdvancedTableToolbar
        filtersOpen={filtersOpen}
        onToggleFilters={toggleFilters}
        activeFilterCount={activeFilterCount}
        columns={schemaColumns}
        sorting={advancedState.sorting}
        onSortingChange={advancedState.setSorting}
        table={table}
        labels={labels}
        isEditMode={isEditMode}
        onToggleEditMode={showEditToggle ? toggleEditMode : undefined}
        isHelpOpen={dataGrid.isHelpOpen}
        onHelpOpenChange={dataGrid.setHelpOpen}
        dataGridLabels={dataGridLabels}
        isFocusMode={isFocusMode}
        onExitFocusMode={exitFocusMode}
      />

      <AdvancedFilterList
        columns={schemaColumns}
        filterRows={advancedState.filterRows}
        setFilterRows={advancedState.setFilterRows}
        isOpen={filtersOpen}
        labels={labels}
        selectOptions={selectOptions}
      />

      <div
        ref={scrollContainerRef}
        data-density={tableDensity}
        className={cn(
          'group/density overflow-auto',
          isFocusMode
            ? 'flex-1 min-h-0 rounded-none border-t border-t-border [&_thead]:bg-neutral-100 [&_thead]:backdrop-blur-none dark:[&_thead]:bg-neutral-900 [&_th]:border-r [&_th]:border-border [&_td]:border-r [&_td]:border-border [&_th:last-child]:border-r-0 [&_td:last-child]:border-r-0'
            : 'max-h-[70vh] rounded-xl border border-border bg-card/50 backdrop-blur-sm shadow-sm',
        )}
        onKeyDown={isEditMode ? dataGrid.handleKeyDown : undefined}
        tabIndex={isEditMode ? 0 : undefined}
        role={isEditMode ? 'grid' : undefined}
      >
        <Table wrapperClassName="overflow-visible" style={{ width: table.getTotalSize() }} className="table-fixed">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{
                      ...getCommonPinningStyles({ column: header.column }),
                      width: header.getSize(),
                    }}
                    className={cn(
                      'relative group/th',
                      header.column.getIsPinned() && 'bg-muted',
                    )}
                    data-pinned={header.column.getIsPinned() || undefined}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanResize() && (
                      <div
                        onDoubleClick={() => header.column.resetSize()}
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        className={cn(
                          'absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none',
                          'opacity-0 group-hover/th:opacity-100',
                          header.column.getIsResizing() && 'opacity-100 bg-sky-500',
                          !header.column.getIsResizing() && 'hover:bg-border',
                        )}
                      />
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          {isVirtualized ? (
            <DataGridVirtualBody {...bodyProps} virtualizer={virtualizer} />
          ) : (
            <DataGridStandardBody {...bodyProps} />
          )}
        </Table>
        {isEditMode && dataGrid.rangeSize && dataGrid.rangeSize.total > 1 && (
          <div className="sticky bottom-2 left-full -translate-x-4 w-fit ml-auto text-xs bg-sky-500 text-white rounded-full px-2 py-0.5 shadow-sm pointer-events-none z-20">
            {dataGrid.rangeSize.rows}×{dataGrid.rangeSize.cols}
          </div>
        )}
      </div>

      <div className={cn('flex flex-col gap-2.5', isFocusMode ? 'px-2 py-1' : 'p-4')}>
        <DataTablePagination table={table} pageSizeOptions={pageSizeOptions} totalItems={total} compact={isFocusMode} />
      </div>

      {doctype && (
        <AdvancedActionBar
          selectedCount={selectedCount}
          onClearSelection={clearSelection}
          onSetStatus={handleSetStatus}
          onExportCSV={handleExportCSV}
          onArchive={handleArchive}
          onRestore={handleRestore}
          showRestore={showArchived}
          labels={labels}
        />
      )}

      {isEditMode && (
        <DataGridPasteDialog
          preview={dataGrid.pastePreview}
          onApply={dataGrid.applyPaste}
          onCancel={dataGrid.cancelPaste}
          isPasting={dataGrid.isPasting}
          progress={dataGrid.pasteProgress}
          labels={dgl}
        />
      )}

      {/* Screen reader announcement for focus mode changes */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {focusAnnouncement}
      </div>
    </div>
  );
}
